import {
	add,
	endOfMonth,
	format,
	isAfter,
	startOfMonth,
	startOfYear,
	sub,
} from "date-fns";
import { useMemo } from "react";

import { useConsumptionData } from "@/api/consumption";
import { useConsumption } from "@/context/ConsumptionContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const today = new Date();

const ConsumptionHousehold = () => {
	// Calculate date ranges based on timeframe
	const yesterday = sub(today, { days: 1 });
	const { selectedDate, timeframe = "Weekly" } = useConsumption();
	const { startDate, endDate } = useMemo(() => {
		let startDate = selectedDate || sub(today, { days: 7 });
		let endDate = startDate;

		if (timeframe === "Weekly") {
			const baseDate = selectedDate || sub(new Date(), { days: 1 });
			endDate = baseDate;
			startDate = sub(endDate, { days: 6 });
		} else if (timeframe === "Monthly") {
			startDate = startOfMonth(startDate);
			endDate = endOfMonth(startDate);

			// Prevent going beyond yesterday
			if (isAfter(endDate, yesterday)) {
				endDate = yesterday;
			}
		} else if (timeframe === "Yearly") {
			startDate = startOfYear(startDate);
			endDate = add(startDate, { years: 1 });
			if (isAfter(endDate, yesterday)) {
				endDate = yesterday;
			}
		}

		return { startDate, endDate };
	}, [selectedDate, timeframe, yesterday]);

	const granularityMap = {
		Weekly: "DAILY",
		Monthly: "DAILY",
		Yearly: "MONTHLY",
	} as const;

	const granularity = granularityMap[timeframe as keyof typeof granularityMap];

	// Fetch consumption data
	const {
		data: consumptionData,
		isLoading,
		error,
	} = useConsumptionData({
		energy: "ELEC",
		granularity,
		start_date: format(startDate, "yyyy-MM-dd"),
		end_date: format(endDate || today, "yyyy-MM-dd"),
		include_baseline: true,
	});

	// Calculate averages across the period
	const { totalConsumption, totalAvg100, totalTop100, totalWorst100 } =
		consumptionData?.data?.reduce(
			(sum, item) => {
				return {
					totalConsumption: sum.totalConsumption + (item?.consumption || 0),
					totalAvg100: sum.totalAvg100 + (item?.average_100_consumption || 0),
					totalTop100: sum.totalTop100 + (item?.top_100_consumption || 0),
					totalWorst100: sum.totalWorst100 + (item?.worst_100_consumption || 0),
				};
			},
			{
				totalConsumption: 0,
				totalAvg100: 0,
				totalTop100: 0,
				totalWorst100: 0,
			},
		) || {
			totalConsumption: 0,
			totalAvg100: 0,
			totalTop100: 0,
			totalWorst100: 0,
		};

	// Process API data
	const processedData = useMemo(() => {
		if (
			!consumptionData?.data ||
			!Array.isArray(consumptionData.data) ||
			consumptionData.data.length === 0
		) {
			return null;
		}

		// Create data array with calculated averages
		return [
			{
				name: "Votre consommation",
				value: Math.round(totalConsumption) || 0,
				color: "#E6007E",
			},
			{
				name: "Les 100 meilleurs",
				value: Math.round(totalTop100) || 0,
				color: "#24395E",
			},
			{
				name: "Moyenne",
				value: Math.round(totalAvg100) || 0,
				color: "#4EBCC3",
			},
			{
				name: "Les 100 moins performants",
				value: Math.round(totalWorst100) || 0,
				color: "#E76E50",
			},
		];
	}, [
		consumptionData,
		totalAvg100,
		totalConsumption,
		totalTop100,
		totalWorst100,
	]);

	if (consumptionData?.data.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	// Loading state
	if (isLoading) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	// Error state
	if (error || !processedData) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	if (timeframe === "Daily") {
		return null;
	}

	const maxValue = Math.max(...processedData.map((item) => item.value));
	const userConsumption =
		processedData.find((item) => item.name === "Votre consommation")?.value ||
		0;
	const averageConsumption =
		processedData.find((item) => item.name === "Moyenne")?.value || 0;

	const indicatorText =
		userConsumption > averageConsumption
			? "Votre consommation est supérieure à la moyenne des foyers similaires de votre région."
			: "Bonne nouvelle ! Votre consommation est inférieure à la moyenne des foyers similaires de votre région.";

	if (totalAvg100 === 0 && totalTop100 === 0 && totalWorst100 === 0) {
		return (
			<Card className="p-3 sm:p-4 md:p-5 gap-4 space-y-4">
				<h2 className="font-semibold text-base sm:text-lg md:text-xl">
					Comparaison avec des foyers similaires
				</h2>

				<div className="border rounded-lg p-2 sm:p-3 md:p-4 mb-3 md:mb-4">
					<div className="flex items-center gap-1 sm:gap-2 md:gap-3">
						<div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex items-center justify-center">
							<span className="text-sm md:text-base">ⓘ</span>
						</div>
						<span className="font-semibold text-sm sm:text-base md:text-lg">
							Un peu de patience
						</span>
					</div>
					<p className="text-xs sm:text-sm md:text-base text-gray-600 mt-0.5 pl-6 sm:pl-8 md:pl-10">
						La comparaison avec les foyers similaires est en cours de calcul et
						sera bientôt disponible !
					</p>
				</div>
			</Card>
		);
	}

	return (
		<Card className="p-3 sm:p-4 md:p-5 gap-4 space-y-4">
			<CardHeader className="flex flex-row items-start justify-between p-0 pb-2">
				<div>
					<CardTitle className="text-md font-semibold">
						Comparaison avec des foyers similaires{" "}
					</CardTitle>
					<p className="text-xs text-gray-400">
						{timeframe === "Weekly"
							? `${format(startDate, "dd/MM/yyyy")} - ${format(endDate, "dd/MM/yyyy")}`
							: timeframe === "Monthly"
								? `${format(startDate, "dd/MM/yyyy")} - ${format(endDate, "dd/MM/yyyy")}`
								: `${format(startDate, "dd/MM/yyyy")} - ${format(endDate, "dd/MM/yyyy")}`}
					</p>
				</div>
			</CardHeader>

			<div className="border rounded-lg p-2 sm:p-3 md:p-4 mb-3 md:mb-4">
				<div className="flex items-center gap-1 sm:gap-2 md:gap-3">
					<div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex items-center justify-center">
						<span className="text-sm md:text-base">ⓘ</span>
					</div>
					<span className="font-semibold text-sm sm:text-base md:text-lg">
						Indicateurs de performance
					</span>
				</div>
				<p className="text-xs sm:text-sm md:text-base text-gray-600 mt-0.5 pl-6 sm:pl-8 md:pl-10">
					{indicatorText}
				</p>
			</div>

			<div className="space-y-2 sm:space-y-3 md:space-y-4 mb-3 sm:mb-4 md:mb-5">
				{processedData.map((item) => (
					<div key={item.name} className="flex flex-col">
						<span className="text-xs sm:text-sm md:text-base mb-0.5 md:mb-1">
							{item.name}
						</span>
						<div
							className="h-6 sm:h-8 md:h-10 rounded-md flex items-center justify-center text-white text-[10px] sm:text-sm md:text-base"
							style={{
								backgroundColor: item.color,
								width: `${(item.value / maxValue) * 100}%`,
								minWidth: "40px",
							}}
						>
							{`${item.value} kWh`}
						</div>
					</div>
				))}
			</div>
		</Card>
	);
};

export default ConsumptionHousehold;
